<template>
  <section id="user-account-access" class="my-8">
    <v-row class="d-flex justify-center">
      <v-col cols="12" md="10" lg="10">
        <card-widget>
          <template #cardBody>
            <loader :loading="isLoading" :size="$appConfig.loader.small" />

            <v-row no-gutters align="start">
              <v-col cols="12" lg="3" md="3">
                <h5
                  class="text-h6 secondary--font font-weight-large text--primary pb-6"
                >
                  {{ $dictionary.app.manageUsers.accounts.title }}
                </h5>
              </v-col>
              <v-spacer />
              <v-col
                cols="12"
                lg="4"
                md="4"
                class="d-flex justify-space-between justify-end overflow-auto"
              >
                <debounce-search-text-field
                  prop-to-search="account_name"
                  placeholder="Search accounts..."
                  @search-value="searchAccount"
                  :lazy-search="() => {}"
                />
              </v-col>
            </v-row>

            <user-accounts-table
              :data="accounts"
              :is-loading="areAccountsLoading"
              @get-records="getUserAccountsList"
              default-sort-order="account_name"
            />
          </template>
        </card-widget>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { CardWidget } from "@/components/widgets";
import { UserAccountsTable } from "@/components/tables";

import { SORTING_ORDERS } from "@/constants";
import { getUserMembership } from "@/services";
import SharedUtilsMixin from "@/mixins/SharedUtils.mixin";

import { DebounceSearchTextField } from "@/components/shared";

/**
 * User account access details
 */
export default {
  name: "UserAccountAccess",
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [SharedUtilsMixin],
  /**
   * ---------------- Props ------------------
   */
  props: {
    user: { type: Object, required: true },
  },
  /**
   * ---------------- Components ------------------
   */
  components: {
    CardWidget,
    UserAccountsTable,
    DebounceSearchTextField,
  },
  /**
   * ---------------- Data Properties ------------------
   */
  data() {
    return {
      accounts: [],
      userAccounts: [],
      isLoading: false,
      areAccountsLoading: false,
      selectedAccounts: [],
      accountsParams: {},
    };
  },
  /**
   * ---------------- Computed Properties ------------------
   */
  computed: {
    ...mapGetters({
      roles: "app/rolesCapitalized",
    }),
    /**
     * Sorting and searching config of user account membership records
     * @type {Object}
     */
    accountsSorting() {
      return this.getSortConfig("accountsParams", SORTING_ORDERS.asc);
    },
    /**
     * Id of the user whose details are being viewed
     */
    userId() {
      return this.$route.params.id;
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    ...mapActions({
      getRoles: "app/getRoles",
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * Search accounts on the basis of search bar account value
     * @param {String} value Account name substring
     */
    searchAccount(value) {
      this.accounts = this.userAccounts.filter((account) => {
        return (
          account.account_name.toLowerCase().indexOf(value.toLowerCase()) > -1
        );
      });
    },
    /**
     * Fetches user accounts (Membership) list
     */
    async getUserAccountsList(params = this.accountsParams) {
      try {
        this.accountsParams = params;
        this.areAccountsLoading = true;
        const { data } = await getUserMembership(this.userId, params);

        this.accounts = data;
        this.userAccounts = data;
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.$errorMessage(error),
          type: this.snackbarTypes.error,
        });
      } finally {
        this.areAccountsLoading = false;
      }
    },
  },
  /**
   * ---------------- Created lifecycle hook ------------------
   */
  created() {
    this.getRoles();
  },
};
</script>
